import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GiftItem, OrderItem, SubscriptionOption } from '../types';
import { formatNaira } from '../lib/data';
import useSEO from '../hooks/useSEO';
import { fetchCategories, useProductCatalog } from '../hooks/useProductCatalog';
import { Button, FlowPage, Icon, Sheet, Stepper, Thumb } from '../components/bx';
import '../styles/bx.css';

interface ItemsPageProps {
  selectedItems: OrderItem[];
  isGetMe?: boolean;
  isSubscribe?: boolean;
  onAddItem: (item: GiftItem, quantity: number, subscriptionOption?: SubscriptionOption) => void;
  onUpdateItems: (items: OrderItem[]) => void;
  onContinue: () => void;
  onBack: () => void;
}

export const flowSteps = (isGetMe?: boolean) => ['Items', 'Vendor', 'Your details', isGetMe ? 'Delivery' : 'Recipient', 'Pay'];
const idOf = (item: GiftItem) => item._id || item.id || '';
const MAX_QTY = 99;

function useDebounced<T>(value: T, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function ItemsPage({ selectedItems, isGetMe, onAddItem, onUpdateItems, onContinue, onBack }: ItemsPageProps) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState<Array<{ category: string; count: number }>>([]);
  const [detail, setDetail] = useState<GiftItem | null>(null);
  const [detailQty, setDetailQty] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [error, setError] = useState('');
  const q = useDebounced(query);
  const { items, loading, error: loadError, hasMore, loadMore, retry } = useProductCatalog({ type: 'product', category, q });
  const sentinel = useRef<HTMLDivElement>(null);

  useSEO({
    title: isGetMe ? 'Shop essentials - BOQBOX' : 'Send a gift - BOQBOX',
    description: isGetMe
      ? 'Fresh groceries, drinks and everyday essentials delivered quietly to your door in Lagos.'
      : 'Pick thoughtful gifts and we’ll deliver them discreetly to someone you love in Lagos.',
  });

  useEffect(() => {
    fetchCategories('product').then(setCategories);
  }, []);

  // Infinite scroll: load the next page as the sentinel approaches the viewport.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadMore(), { rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, items.length]);

  const cartById = useMemo(() => new Map(selectedItems.map((oi) => [idOf(oi.item), oi])), [selectedItems]);
  const count = selectedItems.reduce((n, oi) => n + oi.quantity, 0);
  const subtotal = selectedItems.reduce((sum, oi) => sum + (oi.item.price || 0) * oi.quantity, 0);

  const setQty = useCallback(
    (item: GiftItem, qty: number) => {
      const id = idOf(item);
      if (qty <= 0) {
        onUpdateItems(selectedItems.filter((oi) => idOf(oi.item) !== id));
      } else if (cartById.has(id)) {
        onUpdateItems(selectedItems.map((oi) => (idOf(oi.item) === id ? { ...oi, quantity: Math.min(MAX_QTY, qty) } : oi)));
      } else {
        onAddItem(item, Math.min(MAX_QTY, qty));
      }
      setError('');
    },
    [cartById, onAddItem, onUpdateItems, selectedItems]
  );

  const openDetail = (item: GiftItem) => {
    setDetail(item);
    setDetailQty(cartById.get(idOf(item))?.quantity || 1);
  };

  const handleContinue = () => {
    if (!selectedItems.length) {
      setError(isGetMe ? 'Add at least one item to continue.' : 'Add at least one gift to continue.');
      return;
    }
    setCartOpen(false);
    onContinue();
  };

  const cartList = selectedItems.length ? (
    <div>
      {selectedItems.map((oi) => (
        <div key={idOf(oi.item)} className="bx-cart-line">
          <Thumb src={oi.item.image_url} category={oi.item.category} />
          <div style={{ minWidth: 0 }}>
            <strong>{oi.item.name}</strong>
            <small>{formatNaira((oi.item.price || 0) * oi.quantity)}</small>
          </div>
          <Stepper size="sm" label={oi.item.name} value={oi.quantity} max={MAX_QTY} onChange={(v) => setQty(oi.item, v)} />
        </div>
      ))}
    </div>
  ) : (
    <div className="bx-empty">
      <div className="bx-empty-icon">{isGetMe ? '🛒' : '🎁'}</div>
      <strong style={{ color: 'var(--bx-ink)' }}>Your {isGetMe ? 'cart' : 'gift box'} is empty</strong>
      <span>Tap “Add” on anything you like.</span>
    </div>
  );

  const summary = (
    <div className="bx-card bx-card-pad">
      <div className="bx-between" style={{ marginBottom: 6 }}>
        <h2>{isGetMe ? 'Your cart' : 'Gift box'}</h2>
        {count ? <span className="bx-pill brand">{count} item{count === 1 ? '' : 's'}</span> : null}
      </div>
      {cartList}
      <div className="bx-sum" style={{ marginTop: 14 }}>
        <div className="bx-sum-total"><span>Subtotal</span><span>{formatNaira(subtotal)}</span></div>
        <span className="bx-faint" style={{ fontSize: 12.5 }}>Delivery and fees are shown before you pay.</span>
      </div>
      {error ? <div className="bx-alert" style={{ marginTop: 12 }}>{error}</div> : null}
      <Button block size="lg" style={{ marginTop: 16 }} onClick={handleContinue} disabled={!selectedItems.length}>
        Continue <Icon name="arrow" />
      </Button>
    </div>
  );

  const showSkeleton = loading && items.length === 0;
  const empty = !loading && !loadError && items.length === 0;

  return (
    <FlowPage
      eyebrow={isGetMe ? 'Get me · one-time delivery' : 'Send a gift'}
      title={isGetMe ? 'What do you need today?' : 'Pick something they’ll love'}
      subtitle={isGetMe ? 'Fresh essentials, delivered quietly to your door.' : 'We’ll wrap it in a plain bag and deliver it discreetly.'}
      steps={flowSteps(isGetMe)}
      step={0}
      onBack={onBack}
      backLabel="Home"
      aside={summary}
      barMobileOnly
      headerRight={
        <button type="button" className="bx-icon-btn" onClick={() => setCartOpen(true)} aria-label={`Open cart, ${count} items`}>
          <Icon name="bag" />
          {count ? <span className="bx-icon-badge">{count}</span> : null}
        </button>
      }
      bar={
        <>
          <button type="button" className="bx-bar-copy" style={{ textAlign: 'left', background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer' }} onClick={() => setCartOpen(true)}>
            <small>{count ? `${count} item${count === 1 ? '' : 's'} · view cart` : 'Your cart is empty'}</small>
            <strong>{formatNaira(subtotal)}</strong>
          </button>
          <Button onClick={handleContinue} disabled={!selectedItems.length}>Continue</Button>
        </>
      }
    >
      <div className="bx-stack" style={{ gap: 12, marginBottom: 18 }}>
        <div className="bx-search">
          <Icon name="search" />
          <input className="bx-input" type="search" placeholder="Search bread, eggs, drinks…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search products" />
          {query ? <button type="button" className="bx-search-clear" onClick={() => setQuery('')} aria-label="Clear search">×</button> : null}
        </div>
        {categories.length > 1 ? (
          <div className="bx-chips" role="tablist" aria-label="Categories">
            <button type="button" role="tab" aria-selected={category === 'all'} className={`bx-chip${category === 'all' ? ' on' : ''}`} onClick={() => setCategory('all')}>All</button>
            {categories.map((c) => (
              <button key={c.category} type="button" role="tab" aria-selected={category === c.category} className={`bx-chip${category === c.category ? ' on' : ''}`} onClick={() => setCategory(c.category)}>
                {c.category}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {loadError && items.length === 0 ? (
        <div className="bx-alert" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{loadError}</span>
          <Button size="sm" variant="secondary" onClick={retry}>Try again</Button>
        </div>
      ) : null}

      {empty ? (
        <div className="bx-card bx-empty" style={{ padding: 40 }}>
          <div className="bx-empty-icon"><Icon name="search" /></div>
          <strong style={{ color: 'var(--bx-ink)' }}>No matches{q ? ` for “${q}”` : ''}</strong>
          <span>Try another word or category.</span>
          {(q || category !== 'all') ? <Button size="sm" variant="secondary" onClick={() => { setQuery(''); setCategory('all'); }}>Clear filters</Button> : null}
        </div>
      ) : null}

      <div className="bx-grid" aria-busy={loading}>
        {showSkeleton
          ? Array.from({ length: 6 }, (_, i) => <div key={i} className="bx-skel" style={{ aspectRatio: '3 / 4', borderRadius: 18 }} />)
          : items.map((item) => {
              const inCart = cartById.get(idOf(item));
              return (
                <article key={idOf(item)} className={`bx-product${inCart ? ' in-cart' : ''}`}>
                  <button type="button" className="bx-product-hit" onClick={() => openDetail(item)} aria-label={`View ${item.name}`} />
                  <Thumb src={item.image_url} category={item.category} alt={item.name} />
                  {item.category ? <span className="bx-pill bx-product-cat" style={{ background: 'color-mix(in srgb, var(--bx-surface) 88%, transparent)' }}>{item.category}</span> : null}
                  <div className="bx-product-body">
                    <div className="bx-product-name">{item.name}</div>
                    {item.description ? <div className="bx-product-desc">{item.description}</div> : null}
                    <div className="bx-product-foot">
                      <span className="bx-price">{formatNaira(item.price || 0)}</span>
                      {inCart ? (
                        <Stepper size="sm" tone="brand" label={item.name} value={inCart.quantity} max={MAX_QTY} onChange={(v) => setQty(item, v)} />
                      ) : (
                        <button type="button" className="bx-add" onClick={() => setQty(item, 1)} aria-label={`Add ${item.name}`}>
                          <Icon name="plus" size={16} stroke={2.6} /><span>Add</span>
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
      </div>

      <div ref={sentinel} className="bx-sentinel" />
      {items.length > 0 ? (
        <div className="bx-load-row">
          {loading ? (<><span className="bx-spinner" /> Loading more…</>)
            : loadError ? (<><span>{loadError}</span><Button size="sm" variant="secondary" onClick={retry}>Retry</Button></>)
            : hasMore ? <Button size="sm" variant="secondary" onClick={loadMore}>Show more</Button>
            : <span>You’ve seen everything{q ? ' that matches' : ''}.</span>}
        </div>
      ) : null}

      {detail ? (
        <Sheet
          title={detail.name}
          onClose={() => setDetail(null)}
          footer={
            <div className="bx-row">
              <Stepper label={detail.name} value={detailQty} min={1} max={MAX_QTY} onChange={setDetailQty} />
              <Button block onClick={() => { setQty(detail, detailQty); setDetail(null); }}>
                {cartById.has(idOf(detail)) ? 'Update cart' : 'Add to cart'} · {formatNaira((detail.price || 0) * detailQty)}
              </Button>
            </div>
          }
        >
          <Thumb src={detail.image_url} category={detail.category} alt={detail.name} className="bx-detail-img" />
          <div className="bx-between" style={{ marginTop: 16 }}>
            {detail.category ? <span className="bx-pill" style={{ textTransform: 'capitalize' }}>{detail.category}</span> : <span />}
            <span className="bx-price" style={{ fontSize: 20 }}>{formatNaira(detail.price || 0)}</span>
          </div>
          <p className="bx-muted" style={{ margin: '12px 0 0', lineHeight: 1.6 }}>{detail.description || 'No description yet.'}</p>
          {cartById.has(idOf(detail)) ? (
            <button type="button" className="bx-btn bx-btn-ghost bx-btn-sm" style={{ marginTop: 10, paddingLeft: 0 }} onClick={() => { setQty(detail, 0); setDetail(null); }}>
              Remove from cart
            </button>
          ) : null}
        </Sheet>
      ) : null}

      {cartOpen ? (
        <Sheet
          title={isGetMe ? 'Your cart' : 'Gift box'}
          onClose={() => setCartOpen(false)}
          footer={
            <div className="bx-stack" style={{ gap: 10 }}>
              <div className="bx-sum-total" style={{ borderTop: 0, paddingTop: 0 }}><span>Subtotal</span><span>{formatNaira(subtotal)}</span></div>
              <Button block size="lg" onClick={handleContinue} disabled={!selectedItems.length}>Continue <Icon name="arrow" /></Button>
            </div>
          }
        >
          {cartList}
        </Sheet>
      ) : null}
    </FlowPage>
  );
}
